'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface Metric {
  label: string;
  value: string;
  change: string;
  icon: string;
  trend: 'up' | 'down';
}

interface ChartData {
  name: string;
  before: number;
  after: number;
}

interface ResultsSectionProps {
  metrics: Metric[];
  chartData: ChartData[];
  summary: string;
}

const ResultsSection = ({ metrics, chartData, summary }: ResultsSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-card">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
                Meetbare Resultaten
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {metrics.map((_, index) => (
                <div key={index} className="glassmorphic rounded-xl p-6 h-40 animate-pulse"></div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-card">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Meetbare Resultaten
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              {summary}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {metrics.map((metric, index) => (
              <div
                key={index}
                className="glassmorphic rounded-xl p-6 hover:shadow-premium transition-all duration-300 group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                    <Icon name={metric.icon as any} size={24} className="text-primary" />
                  </div>
                  <span className={`flex items-center space-x-1 text-sm font-semibold ${
                    metric.trend === 'up' ? 'text-success' : 'text-error'
                  }`}>
                    <Icon 
                      name={metric.trend === 'up' ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'} 
                      size={16} 
                    />
                    <span>{metric.change}</span>
                  </span>
                </div>
                <div className="text-3xl font-display font-bold text-foreground mb-2">
                  {metric.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
          
          <div className="glassmorphic rounded-xl p-8">
            <h3 className="text-2xl font-display font-bold text-foreground mb-6 text-center">
              Voor & Na Vergelijking
            </h3>
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  barSize={60}
                >
                  <defs>
                    <linearGradient id="beforeGradientCase" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#9CA3AF" stopOpacity={1} />
                      <stop offset="100%" stopColor="#6B7280" stopOpacity={0.8} />
                    </linearGradient>
                    <linearGradient id="afterGradientCase" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00E5FF" stopOpacity={1} />
                      <stop offset="100%" stopColor="#00D4FF" stopOpacity={0.9} />
                    </linearGradient>
                    <filter id="shadowCase">
                      <feDropShadow dx="0" dy="4" stdDeviation="4" floodOpacity="0.3" />
                    </filter>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="rgba(255,255,255,0.08)" 
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="name" 
                    stroke="#E5E7EB"
                    tick={{ fill: '#E5E7EB', fontSize: 14, fontWeight: 600 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#E5E7EB"
                    tick={{ fill: '#E5E7EB', fontSize: 12 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                    tickLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(26, 26, 26, 0.95)', 
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      boxShadow: '0 8px 32px rgba(0, 212, 255, 0.2)',
                    }}
                    labelStyle={{ color: '#F8F9FA', fontWeight: 600, marginBottom: '8px' }}
                    itemStyle={{ color: '#E5E7EB', padding: '4px 0' }}
                    cursor={{ fill: 'rgba(0, 212, 255, 0.05)' }}
                  />
                  <Bar 
                    dataKey="before" 
                    fill="url(#beforeGradientCase)" 
                    name="Voor" 
                    radius={[12, 12, 0, 0]}
                    filter="url(#shadowCase)"
                    animationDuration={1200}
                    animationBegin={0}
                  />
                  <Bar 
                    dataKey="after" 
                    fill="url(#afterGradientCase)" 
                    name="Na" 
                    radius={[12, 12, 0, 0]}
                    filter="url(#shadowCase)"
                    animationDuration={1200}
                    animationBegin={200}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResultsSection;